import { Icon, ICONS, mixins } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapLesson } from '@websrc/DataMappers/mapLesson'
import React, { ReactNode, useContext } from 'react'

import * as SC from './LessonTeacherSupport.style'

type AdditionalResources = ReturnType<typeof mapLesson>['teacherSupport']['additionalResources'][0]

export interface AccordionItemProps {
  label: string
  children?: ReactNode
}

function AccordionItem({ label, children }: AccordionItemProps) {
  const { matomoPageTemplate } = useContext(AppProviderContext)
  const t = useStaticText()

  return (
    <SC.Accordion
      button={(isActive: boolean) => {
        if (isActive) {
          matomo.trackEvent(
            'engagement',
            matomoPageTemplate,
            [t.teacher_support.teacher_support],
            [label],
            'drop-down list'
          )
        }

        return (
          <SC.ItemLabel>
            {label} <Icon icon={isActive ? ICONS.ARROW_UP : ICONS.ARROW_DOWN} size={20} />
          </SC.ItemLabel>
        )
      }}
    >
      <SC.ItemContent>{children}</SC.ItemContent>
    </SC.Accordion>
  )
}

export interface LessonTeacherSupportProps {
  keyObjectives: string
  thingsYouWillNeed?: string | null
  additionalResources?: AdditionalResources[]
  educationalStandards?: string | null
  hybridLearningResources?: AdditionalResources[]
}

export const LessonTeacherSupport = ({
  keyObjectives,
  thingsYouWillNeed,
  additionalResources,
  educationalStandards,
  hybridLearningResources,
}: LessonTeacherSupportProps) => {
  const t = useStaticText()

  return (
    <SC.Wrapper data-testid={'teacher_support'}>
      <SC.Title>{t.teacher_support.teacher_support}</SC.Title>
      <AccordionItem data-testid={'key_objectives'} label={t.teacher_support.key_objectives}>
        <SC.Content color={mixins.colors.textSecondary} markdown={keyObjectives} smallBody={true} />
      </AccordionItem>
      {!!thingsYouWillNeed && (
        <AccordionItem
          data-testid={'things_you_will_need'}
          label={t.teacher_support.things_you_will_need}
        >
          <SC.Content
            color={mixins.colors.textSecondary}
            markdown={thingsYouWillNeed}
            smallBody={true}
          />
        </AccordionItem>
      )}
      {!!additionalResources?.length && (
        <AccordionItem
          data-testid={'additional_resources'}
          label={t.teacher_support.additional_resources}
        >
          {additionalResources?.map((resource, i) =>
            resource.resource ? (
              <SC.AccordionLink href={resource.resource.url} key={i} target={'_blank'}>
                {resource.resource_name}
              </SC.AccordionLink>
            ) : (
              <p key={i}>{resource.resource_name}</p>
            )
          )}
        </AccordionItem>
      )}
      {!!educationalStandards && (
        <AccordionItem
          data-testid={'educational_standards'}
          label={t.teacher_support.educational_standards}
        >
          <SC.Content
            color={mixins.colors.textSecondary}
            markdown={educationalStandards}
            smallBody={true}
          />
        </AccordionItem>
      )}
      {!!hybridLearningResources?.length && (
        <AccordionItem
          data-testid={'student_material'}
          label={t.teacher_support.hybrid_learning_resources}
        >
          {hybridLearningResources?.map((resource, i) =>
            resource.resource ? (
              <SC.AccordionLink href={resource.resource.url} key={i} target={'_blank'}>
                {resource.resource_name}
              </SC.AccordionLink>
            ) : (
              <p key={i}>{resource.resource_name}</p>
            )
          )}
        </AccordionItem>
      )}
    </SC.Wrapper>
  )
}
